import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  FormControl,
  FormLabel,
  Input,
  VStack,
  FormErrorMessage,
  InputGroup,
  InputRightAddon,
} from '@chakra-ui/react';

const TaxaModal = ({ isOpen, onClose, taxa, onSave }) => {
  const [formData, setFormData] = useState({
    ano: '',
    taxa: '',
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  // Initialize form when taxa changes
  useEffect(() => {
    if (taxa) {
      setFormData({
        ano: taxa.ano.toString(),
        // Convert decimal to percentage with comma (0.105 -> 10,5)
        taxa: (taxa.taxa * 100).toString().replace('.', ','),
      });
    } else {
      setFormData({
        ano: '',
        taxa: '',
      });
    }
    setErrors({});
  }, [taxa, isOpen]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: '',
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Validate year
    if (!formData.ano) {
      newErrors.ano = 'Ano é obrigatório';
    } else {
      const year = parseInt(formData.ano);
      if (isNaN(year) || year < 1900 || year > 2100) {
        newErrors.ano = 'Ano deve estar entre 1900 e 2100';
      }
    }

    // Validate taxa
    if (!formData.taxa) {
      newErrors.taxa = 'Taxa é obrigatória';
    } else {
      // Replace comma with dot for validation
      const taxaValue = parseFloat(formData.taxa.replace(',', '.'));
      if (isNaN(taxaValue) || taxaValue < 0 || taxaValue > 100) {
        newErrors.taxa = 'Taxa deve ser um número entre 0 e 100';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      // Convert percentage with comma to decimal (10,5 -> 0.105)
      const taxaDecimal = parseFloat(formData.taxa.replace(',', '.')) / 100;

      const data = {
        ano: parseInt(formData.ano),
        taxa: taxaDecimal,
      };

      await onSave(data);
      handleClose();
    } catch (error) {
      console.error('Error saving taxa:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      ano: '',
      taxa: '',
    });
    setErrors({});
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      size={{ base: 'full', md: 'md' }}
    >
      <ModalOverlay />
      <ModalContent
        as="form"
        onSubmit={(e) => {
          e.preventDefault();
          handleSubmit();
        }}
      >
        <ModalHeader>
          {taxa ? 'Editar Taxa' : 'Nova Taxa'} Selic/IPCA
        </ModalHeader>
        <ModalCloseButton />

        <ModalBody>
          <VStack spacing={4}>
            <FormControl isInvalid={!!errors.ano}>
              <FormLabel>Ano</FormLabel>
              <Input
                name="ano"
                type="number"
                value={formData.ano}
                onChange={handleChange}
                placeholder="Ex: 2024"
                min="1900"
                max="2100"
              />
              <FormErrorMessage>{errors.ano}</FormErrorMessage>
            </FormControl>

            <FormControl isInvalid={!!errors.taxa}>
              <FormLabel>Taxa</FormLabel>
              <InputGroup>
                <Input
                  name="taxa"
                  type="text"
                  value={formData.taxa}
                  onChange={handleChange}
                  placeholder="Ex: 10,5"
                />
                <InputRightAddon>%</InputRightAddon>
              </InputGroup>
              <FormErrorMessage>{errors.taxa}</FormErrorMessage>
            </FormControl>
          </VStack>
        </ModalBody>

        <ModalFooter>
          <Button type="button" variant="ghost" mr={3} onClick={handleClose}>
            Cancelar
          </Button>
          <Button
            type="submit"
            colorScheme="purple"
            onClick={handleSubmit}
            isLoading={loading}
          >
            {taxa ? 'Atualizar' : 'Criar'}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default TaxaModal;
